import React from 'react';
import { Slider, Button, Tooltip } from 'antd';
import { useFieldArray, Control, useWatch, FieldErrors, FieldError, Controller } from 'react-hook-form';
import { Input } from '../../../common/CommonExport';
import { DesignationProps, KPIItem } from '../OrganisationsInterfaces';
import './KPISection.scss';

interface KPISectionProps {
  control: Control<DesignationProps>;
  errors: FieldErrors<DesignationProps>;
  isReadOnly?: boolean;
}

const KPISection: React.FC<KPISectionProps> = ({ control, errors, isReadOnly }) => {
  const readOnly = Boolean(isReadOnly);
  const { fields, append, remove } = useFieldArray({
    control,
    name: "kpiQuestionaires"
  });

  const kpiList = useWatch({
    control,
    name: "kpiQuestionaires",
    defaultValue: []
  });

  const safeKpiList = kpiList || [];
  const totalWeightage = safeKpiList.reduce((acc: number, item: KPIItem) => acc + (item.kpiWeightage || 0), 0);
  const isOver100 = totalWeightage > 100;

  return (
    <div className="flex flex-col h-full kpi-section">

      {/* ── Header ── */}
      <div className="kpi-header">
        <h3 className="kpi-title ">KPI </h3>

        <span className={`kpi-total-text ${isOver100 ? 'over' : ''}`}>
          Total Weightage: <strong>{totalWeightage}%</strong>
          {isOver100 && <span className="kpi-over-warning"> (Exceeds 100%!)</span>}
        </span>
      </div>

      {/* ── KPI List ── */}
      <div className="kpi-list">
        {fields.map((field, index) => {
          const item = safeKpiList[index] || {};
          const isInputsFilled =
            (item.kpiQuestionaire || '').trim() !== '' &&
            (item.kpiDescription || '').trim() !== '';

          const otherItemsWeightage = safeKpiList.reduce((acc: number, kpi: KPIItem, idx: number) =>
            idx !== index ? acc + (kpi.kpiWeightage || 0) : acc, 0);
          const maxAvailable = Math.max(0, 100 - otherItemsWeightage);

          const canEditWeightage = !readOnly && isInputsFilled;

          const kpiErrors = errors.kpiQuestionaires?.[index];
          const questionError = kpiErrors?.kpiQuestionaire?.message;
          const descriptionError = kpiErrors?.kpiDescription?.message;

          return (
            <div key={field.id} className="kpi-row group">

              {/* Remove button — top right corner */}
              <button
                type="button"
                onClick={() => remove(index)}
                className="kpi-remove-btn"
                title="Remove KPI"
                disabled={readOnly}
              >
                <span className="material-symbols-outlined">close</span>
              </button>

              {/* Row middle: two text inputs side by side */}
              <div className="kpi-inputs-grid">
                <div className="relative z-0 form-item font-rubik">
                  <Input
                    type="text"
                    id={`kpiQuestionaire-${index}`}
                    name={`kpiQuestionaires.${index}.kpiQuestionaire`}
                    label="KRA*"
                    placeholder=" "
                    control={control}
                    hasError={!!questionError}
                    disabled={readOnly}
                  />
                  {questionError && (
                    <span className="kpi-field-error">{questionError}</span>
                  )}
                </div>

                <div className="relative z-0 form-item font-rubik">
                  <Input
                    type="text"
                    id={`kpiDescription-${index}`}
                    name={`kpiQuestionaires.${index}.kpiDescription`}
                    label="KPI (Detailed)*"
                    placeholder=" "
                    control={control}
                    hasError={!!descriptionError}
                    disabled={readOnly}
                  />
                  {descriptionError && (
                    <span className="kpi-field-error">{descriptionError}</span>
                  )}
                </div>
              </div>

              {/* Row bottom: weightage input + slider */}
              <div className={`kpi-weightage-row ${!canEditWeightage ? 'disabled' : ''}`}>
                <span className="kpi-weightage-label">Weightage</span>

                <Controller
                  control={control}
                  name={`kpiQuestionaires.${index}.kpiWeightage`}
                  render={({ field: { value, onChange } }) => {
                    const currentVal = value || 0;

                    const handleSliderChange = (val: number) => {
                      onChange(Math.min(val, maxAvailable));
                    };

                    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                      const raw = e.target.value.replace(/[^0-9]/g, ''); // digits only
                      if (raw === '') { onChange(0); return; }
                      const parsed = parseInt(raw, 10);
                      if (isNaN(parsed)) return;
                      onChange(Math.min(Math.max(0, parsed), maxAvailable));
                    };

                    return (
                      <div className="kpi-weightage-controls">
                        {/* Number input */}
                        <div className="kpi-number-input-wrap">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min={0}
                            max={maxAvailable}
                            value={currentVal === 0 ? '' : String(currentVal)}
                            placeholder="0"
                            onChange={handleInputChange}
                            disabled={!canEditWeightage}
                            className="kpi-number-input"
                            title={`Max allowed: ${maxAvailable}%`}
                          />
                          <span className="kpi-percent-sign">%</span>
                        </div>

                        {/* Slider */}
                        <div className="kpi-slider-wrap">
                          <Slider
                            min={0}
                            max={maxAvailable}
                            value={currentVal}
                            onChange={handleSliderChange}
                            tooltip={{ formatter: (val) => `${val}%` }}
                            disabled={!canEditWeightage}
                          />
                        </div>

                        {/* Max hint */}
                        {canEditWeightage && maxAvailable < 100 && (
                          <span className="kpi-max-hint">Max: {maxAvailable}%</span>
                        )}
                      </div>
                    );
                  }}
                />
              </div>

            </div>
          );
        })}

        {fields.length === 0 && (
          <div className="kpi-empty-state">
            No KPIs added yet. Click "Add KPI" to get started.
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="kpi-footer">
        {(() => {
          const lastKPI = safeKpiList[safeKpiList.length - 1];
          const lastKPIHasWeightage = !lastKPI || (lastKPI.kpiWeightage || 0) > 0;
          const canAddKPI = totalWeightage < 100 && lastKPIHasWeightage;

          let tooltipMessage = '';
          if (totalWeightage >= 100) {
            tooltipMessage = "Total weightage is already 100%. Remove or adjust existing KPIs to add new ones.";
          } else if (!lastKPIHasWeightage) {
            tooltipMessage = "Please set weightage for the current KPI before adding a new one.";
          }

          return (
            <Tooltip title={tooltipMessage}>
              <Button
                type="link"
                onClick={() => append({ kpiQuestionaire: '', kpiDescription: '', kpiWeightage: 0 })}
                size="small"
                disabled={readOnly || !canAddKPI}
                className="kpi-add-btn"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Add KPI
              </Button>
            </Tooltip>
          );
        })()}

        <div className="kpi-footer-errors">
          {(errors.kpiQuestionaires as unknown as FieldError)?.message && (
            <span className="kpi-error-msg">
              {(errors.kpiQuestionaires as unknown as FieldError).message}
            </span>
          )}
          {errors.kpiQuestionaires?.root?.message && (
            <span className="kpi-error-msg">
              {errors.kpiQuestionaires.root.message}
            </span>
          )}
        </div>
      </div>

    </div>
  );
};

export default KPISection;
